/**
 * @fileoverview Standalone Angular 21 Component for Chat V2 Message Bubble.
 * Implements the streamlined message container matching the Flutter mobile reference:
 * - Zero empty bubble containers while streaming or executing tools before text arrives.
 * - Vertical stacking order: Reasoning Block above -> Message Content -> Unified Action Cards at end.
 * - Full web developer tooling preservation: Agent Debug Drawer, Monaco Code Editor, and JSON viewers.
 * - Action execution delegation without redundant visible chat bubbles.
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  signal,
  computed,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { MarkdownPreviewComponent } from '@rapider/markdown-preview';
import {
  NotificationService,
  BrowserStorageManagementService,
} from '@rapider/services';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TemplateRendererComponent } from '@rapider/template-renderer';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ProjectDataActions,
  selectProjectModelsList,
} from '@rapider/project-data';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { environment } from '@rapider/environments';

import {
  AIChatMessage,
  AiAction,
  ReasoningStep,
  FileAttachment,
  WorkflowPlan,
  formatMessageTime,
  getFileIconClass,
  formatFileSize,
  createPlayableAudioUrl,
  isBrowserPreviewable,
  forceDownloadFile,
  isServerAction as isBaseServerAction,
  InteractionOption,
  extractListGridConfig,
  generateDefaultListGridConfig,
  getWorkflowTaskTheme,
  hasEndpointAction,
  generateTempId,
  MemoryUpdate,
  AI_CHAT_CONFIG,
  buildMemoryNoteQueryParams,
  memoryNoteLabel,
  buildExecuteAllTooltip,
} from '../../../ai-chat.config';

import { WorkItemControllerService } from '@rapider/sdk';
import { AiChatActions } from '../../../state/ai-chat.actions';
import {
  selectIsAiThinking,
  selectActiveThreadId,
  selectProjectModels,
  selectLoadingModels,
  selectMessages,
  selectThreads,
} from '../../../state/ai-chat.reducer';
import { WorkflowExecutionService } from '../../../services/workflow-execution.service';

import {
  ChatV2ReasoningBlockComponent,
  ChatV2ReasoningStep,
} from '../chat-v2-reasoning-block/chat-v2-reasoning-block.component';
import {
  ChatV2ActionCardComponent,
  ChatV2ActionExecuteEvent,
  ChatV2ActionToggleEvent,
} from '../chat-v2-action-card/chat-v2-action-card.component';
import { ChatV2AnimationPipeline } from '../../services/chat-v2-animation.pipeline';
import { AgentDebugDrawerComponent } from '../../../agent-debug-drawer/agent-debug-drawer.component';
import { ChatInteractionWidgetComponent } from '../../../chat-interaction-widget/chat-interaction-widget.component';
import { ChatToolCallCardComponent } from '../../../chat-tool-call-card/chat-tool-call-card.component';
import { ChatAsyncProgressCardComponent } from '../../../chat-async-progress-card/chat-async-progress-card.component';
import { ChatEntityDataCardComponent } from '../../../chat-entity-data-card/chat-entity-data-card.component';
import { ChatWorkflowPlanCardComponent } from '../../../chat-workflow-plan-card/chat-workflow-plan-card.component';

/**
 * Configuration constants and default strings for Chat V2 Message Bubble.
 * Extracted into a pure object to prevent hardcoded strings in logic per architecture rules.
 */
export const CHAT_V2_MESSAGE_BUBBLE_CONFIG = {
  texts: {
    agentDefaultName: 'Rapider AI',
    copyAction: 'Copy text',
    copiedAction: 'Copied!',
    inspectAiResponse: 'Inspect AI Response',
    voiceMessageLabel: 'Voice Message',
    voiceExpiredError: 'Audio expired or unavailable',
    closePreviewBtn: 'Close',
    downloadBtn: 'Download',
    audioUnavailableTitle: 'Audio Unavailable',
    workflowExecutionConfirmed: 'Workflow execution confirmed.',
    batchRun: 'Run',
    batchActionSingular: 'action',
    batchActionPlural: 'actions',
    batchReject: 'Reject',
    executeAll: 'Execute All',
    executeAllActions: 'Execute All Actions',
    executeAction: 'Execute Action',
    discardAll: 'Discard All',
  },
  classes: {
    userBubble:
      'bg-alt-background border border-primary-border text-primary-text rounded-2xl rounded-tr-sm',
    aiBubble:
      'bg-component-inner text-primary-text rounded-2xl rounded-tl-sm border border-secondary-border shadow-xs',
  },
  clipboardTimeoutMs: 2000,
} as const;

/**
 * PURE FUNCTION: Evaluates whether the visual message bubble container should be painted.
 * Prevents rendering empty white containers during preliminary thinking/tool streaming phases.
 *
 * @param isUser - Whether the message was authored by the user.
 * @param text - The raw text or markdown body of the message.
 * @param isVoiceMessage - Whether the message represents an audio voice recording.
 * @returns True if the bubble container should be painted, false otherwise.
 */
export function shouldRenderBubble(
  isUser: boolean,
  text?: string | null,
  isVoiceMessage?: boolean,
  hasExtraContent = false,
): boolean {
  if (isUser) {
    return true;
  }
  if (text && text.trim().length > 0) {
    return true;
  }
  if (isVoiceMessage) {
    return true;
  }
  if (hasExtraContent) {
    return true;
  }
  return false;
}

/**
 * PURE FUNCTION: Resolves whether the current message is actively streaming.
 * Checks explicit message streaming flag first, then falls back to store thinking state for the latest message.
 *
 * @param messageStreaming - Explicit streaming flag on the message entity.
 * @param isLatest - Whether this message is the most recent in the thread.
 * @param isUser - Whether this message was authored by the user.
 * @param isThinking - Global store state indicating if AI is generating an answer.
 * @returns True if streaming is in progress.
 */
export function resolveIsStreaming(
  messageStreaming: boolean | undefined,
  isLatest: boolean,
  isUser: boolean,
  isThinking: boolean,
): boolean {
  if (messageStreaming !== undefined) {
    return Boolean(messageStreaming);
  }
  return isLatest && !isUser && isThinking;
}

/**
 * PURE FUNCTION: Filters out the primary audio file attachment from voice messages
 * so it is not rendered redundantly in both the voice player and attachment chips.
 *
 * @param attachments - Full list of file attachments on the message.
 * @param isVoiceMessage - Whether the message is an audio voice message.
 * @param voiceFileId - Optional backend reference file ID for the voice audio.
 * @returns Filtered array of non-voice file attachments.
 */
export function filterVisibleAttachments(
  attachments: FileAttachment[] | undefined,
  isVoiceMessage?: boolean,
  voiceFileId?: string,
): FileAttachment[] {
  if (!attachments || attachments.length === 0) {
    return [];
  }
  if (!isVoiceMessage) {
    return attachments;
  }

  return attachments.filter((file) => {
    if (voiceFileId && file.id === voiceFileId) {
      return false;
    }
    const mime = file.mimeType?.toLowerCase() || '';
    const isAudioType =
      mime.startsWith('audio/') || mime.startsWith('video/ogg');
    const name = file.name?.toLowerCase() || '';
    const isAudioExt =
      name.endsWith('.wav') ||
      name.endsWith('.mp3') ||
      name.endsWith('.m4a') ||
      name.endsWith('.ogg');
    if (!voiceFileId && (isAudioType || isAudioExt)) {
      return false;
    }
    return true;
  });
}

/**
 * PURE FUNCTION: Maps generic AIChatMessage reasoning steps into the ChatV2ReasoningStep contract.
 *
 * @param steps - Base reasoning steps from AIChatMessage.
 * @returns Normalized ChatV2ReasoningStep array.
 */
export function mapToV2ReasoningSteps(
  steps: ReasoningStep[] | undefined,
): ChatV2ReasoningStep[] {
  if (!steps || steps.length === 0) {
    return [];
  }
  return steps.map((step, idx) => ({
    id: step.id || `step-${idx}`,
    title: step.title,
    content: step.content,
    status: step.status,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subtitle: (step as any).subtitle,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    capabilityName: (step as any).capabilityName,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: (step as any).data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: (step as any).payload,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eventType: (step as any).eventType,
  }));
}

/**
 * PURE FUNCTION: Formats a date or date string into a user-friendly timestamp label.
 *
 * @param timestamp - Message timestamp date or string.
 * @returns Human-readable formatted time string.
 */
export function formatBubbleTimestamp(
  timestamp: Date | string | undefined,
): string {
  if (!timestamp) {
    return '';
  }
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return formatMessageTime(date);
}

/** One open action of a batch with the decision taken for it. */
export interface ChatV2BatchDecision {
  /** The action. */
  action: AiAction;
  /** Its index within the message's visible actions. */
  index: number;
  /** True when it runs / is confirmed, false when it is declined or skipped. */
  isActive: boolean;
}

/**
 * PURE FUNCTION: the open (not yet completed) actions of a batch with their decision.
 *
 * @param actions The message's visible actions.
 * @param isRunning Whether the action at an index runs (switched on and the batch is run).
 * @param isCompleted Whether an action already finished (it is left out).
 * @returns The decisions, in action order.
 */
export function toBatchDecisions(
  actions: readonly AiAction[],
  isRunning: (index: number) => boolean,
  isCompleted: (action: AiAction) => boolean
): ChatV2BatchDecision[] {
  return actions
    .map((action, index) => ({ action, index, isActive: isRunning(index) }))
    .filter(({ action }) => !isCompleted(action));
}

/**
 * PURE FUNCTION: whether an action is an async workflow run (confirmed by a hidden message rather
 * than executed by the endpoint executor). The run flag is optional wire data not declared on
 * `AiAction`, so it is read through a widened type.
 *
 * @param action The action.
 * @returns True for a workflow run.
 */
export function isWorkflowRun(action: AiAction): boolean {
  return action.type === 'workflow' || (action as AiAction & { isWorkflowRunAction?: boolean }).isWorkflowRunAction === true;
}

/**
 * PURE FUNCTION: how many open actions are switched on.
 *
 * @param actions The message's visible actions.
 * @param isActive Whether the action at an index is switched on.
 * @param isCompleted Whether an action already finished.
 * @returns The count.
 */
export function countRunnableActions(
  actions: readonly AiAction[],
  isActive: (index: number) => boolean,
  isCompleted: (action: AiAction) => boolean
): number {
  return toBatchDecisions(actions, isActive, isCompleted).filter((decision) => decision.isActive).length;
}

/**
 * PURE FUNCTION: label of the batch run button ("Run 1 action", "Run 3 actions").
 *
 * @param count Number of actions that will run.
 * @param texts Copy (`batchRun`, `batchActionSingular`, `batchActionPlural`).
 * @returns The label.
 */
export function formatBatchRunLabel(
  count: number,
  texts: { batchRun: string; batchActionSingular: string; batchActionPlural: string }
): string {
  return `${texts.batchRun} ${count} ${count === 1 ? texts.batchActionSingular : texts.batchActionPlural}`;
}

/**
 * PURE FUNCTION: Formats the Execute All button label based on the count of active actions.
 * Supports localization keys and provides clean, user-friendly labels.
 *
 * @param count - Number of actions that will be executed.
 * @param texts - Config texts object containing localization strings.
 * @returns Human-readable button label (e.g. "Execute Action", "Execute All (3)").
 */
export function formatExecuteAllLabel(
  count: number,
  texts: {
    executeAction?: string;
    executeAll?: string;
    executeAllActions?: string;
    batchRun?: string;
    batchActionSingular?: string;
    batchActionPlural?: string;
  },
): string {
  if (count === 1) {
    return texts.executeAction || texts.batchRun || 'Execute Action';
  }
  const prefix = texts.executeAll || texts.executeAllActions || 'Execute All';
  return `${prefix} (${count})`;
}

/**
 * @class ChatV2MessageBubbleComponent
 * @description Standalone Angular 21 Component rendering streamlined chat message bubbles in V2.
 * Enforces:
 * 1. Zero empty bubble containers while thinking or executing tools before text has arrived.
 * 2. Exact mobile parity stacking order: Reasoning Block above, Bubble content inside, Action cards at end.
 * 3. Full web developer tooling preservation: Monaco editor, Agent Debug Drawer, JSON tree inspection.
 * 4. Action execution delegation without redundant visible chat bubbles.
 */
@Component({
  selector: 'rapider-chat-v2-message-bubble',
  standalone: true,
  imports: [
    CommonModule,
    NzTooltipModule,
    NzModalModule,
    NzButtonModule,
    NzTagModule,
    MarkdownPreviewComponent,
    ChatV2ReasoningBlockComponent,
    ChatV2ActionCardComponent,
    AgentDebugDrawerComponent,
    ChatInteractionWidgetComponent,
    ChatToolCallCardComponent,
    ChatAsyncProgressCardComponent,
    ChatEntityDataCardComponent,
    ChatWorkflowPlanCardComponent,
    TemplateRendererComponent,
  ],
  templateUrl: './chat-v2-message-bubble.component.html',
  styleUrls: ['./chat-v2-message-bubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatV2MessageBubbleComponent
  implements OnInit, OnChanges, OnDestroy
{
  // Injected dependencies via Angular 21 inject() pattern
  private readonly store = inject(Store);
  private readonly router = inject(Router, { optional: true });
  private readonly browserStorage = inject(BrowserStorageManagementService, {
    optional: true,
  });
  private readonly workflowExecutionService = inject(WorkflowExecutionService);
  private readonly notificationService = inject(NotificationService);
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly workItemService = inject(WorkItemControllerService, {
    optional: true,
  });

  /** Store selector signals */
  private readonly isAiThinkingSignal =
    this.store.selectSignal(selectIsAiThinking);
  private readonly activeThreadIdSignal =
    this.store.selectSignal(selectActiveThreadId);

  /** Authoritative configuration object exposed for template consumption */
  public readonly config = CHAT_V2_MESSAGE_BUBBLE_CONFIG;

  /**
   * Opens a note the runtime saved during this turn in the Knowledge notes view (the private
   * knowledge view for notes saved to the person's own space).
   *
   * @param note The saved note (`message.memoryUpdates` entry).
   */
  public openMemoryNote(note: MemoryUpdate): void {
    void this.router?.navigate([...AI_CHAT_CONFIG.constants.knowledgeNotesRoute], { queryParams: buildMemoryNoteQueryParams(note) });
  }

  /**
   * PURE FUNCTION: label of a saved-note chip (company vs private knowledge).
   *
   * @param note The saved note.
   * @returns The localized chip label.
   */
  public memoryLabel(note: MemoryUpdate): string {
    return memoryNoteLabel(note);
  }

  /** Phased typewriter animation pipeline */
  public readonly pipeline = new ChatV2AnimationPipeline();

  /**
   * The message model to render.
   */
  @Input({ required: true }) message!: AIChatMessage;

  /**
   * Whether this message bubble is the latest one in the thread.
   */
  @Input() isLatest = false;

  /**
   * Optional display name for the AI agent, defaults to 'Rapider AI'.
   */
  @Input() agentName = CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts.agentDefaultName;

  /**
   * Optional explicit override for streaming status.
   */
  @Input() isStreamingOverride?: boolean;

  /**
   * Emitted when user initiates action execution.
   */
  @Output() onExecute = new EventEmitter<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any;
    confirmed: boolean;
    index: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options?: any;
  }>();

  /** Standard output alias for onExecute */
  @Output() execute = this.onExecute;

  /**
   * Emitted when user toggles an action's active/inactive switch.
   */
  @Output() onActionToggle = new EventEmitter<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any;
    isActive: boolean;
    index: number;
  }>();

  /** Standard output alias for onActionToggle */
  @Output() actionToggle = this.onActionToggle;

  /**
   * Emitted on each typewriter advance or phase reveal to drive smooth container auto-scroll (matching mobile onTypingProgress).
   */
  @Output() onTypingProgress = new EventEmitter<void>();

  /** Standard output alias for onTypingProgress */
  @Output() typingProgress = this.onTypingProgress;

  /**
   * Emitted when all message entrance animations have completed (matching mobile onAnimationComplete).
   */
  @Output() onAnimationComplete = new EventEmitter<AIChatMessage>();

  /** Standard output alias for onAnimationComplete */
  @Output() animationComplete = this.onAnimationComplete;

  // Reactive state signals
  public readonly isDebugDrawerOpen = signal<boolean>(false);
  public readonly isCopied = signal<boolean>(false);
  public readonly localVoiceUrl = signal<string | undefined>(undefined);
  public readonly isLoadingAudio = signal<boolean>(false);
  public readonly audioError = signal<boolean>(false);
  public readonly autoPlayAudio = signal<boolean>(false);
  public readonly isPreviewModalVisible = signal<boolean>(false);
  public readonly previewFile = signal<FileAttachment | null>(null);
  public readonly inactiveActionIndices = signal<Set<number>>(new Set());
  /** Set once the batch bar ran or rejected this message's actions (optimistic: hides the bar at once). */
  public readonly batchSubmitted = signal<boolean>(false);

  /**
   * PURE GETTER: Evaluates whether the visual bubble container should be painted.
   * Conforms strictly to Requirement 1:
   * Returns true for user messages, non-empty AI text, voice messages, actions, or entity/workflow cards.
   * Returns false during preliminary reasoning/tool streaming before text or data arrives.
   */
  public get shouldRenderBubbleContainer(): boolean {
    if (this.message.isUser) {
      return true;
    }
    if (this.message.text && this.message.text.trim().length > 0) {
      return true;
    }
    if (this.message.isVoiceMessage) {
      return true;
    }
    if (this.hasActions) {
      return true;
    }
    if (this.message.entityData) {
      return true;
    }
    if (this.message.workflowPlan) {
      return true;
    }
    if (this.message.jsonataTransformation) {
      return true;
    }
    if (this.message.template || (this.message as any)?.displayTemplate) {
      return true;
    }
    if (this.message.displayComponent) {
      return true;
    }
    return false;
  }

  /**
   * Evaluates whether the current message is actively streaming response or activity events.
   */
  public get isStreaming(): boolean {
    if (this.isStreamingOverride !== undefined) {
      return this.isStreamingOverride;
    }
    return resolveIsStreaming(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this.message as any)?.isStreaming,
      this.isLatest,
      this.message.isUser,
      this.isAiThinkingSignal(),
    );
  }

  /**
   * Evaluates whether the reasoning block should be mounted above the message bubble.
   */
  public get hasReasoning(): boolean {
    if (this.message.isUser) {
      return false;
    }
    const steps = this.message.reasoningSteps;
    const hasSteps = Boolean(steps && steps.length > 0);
    const isStream = this.isStreaming;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const forceShimmer = Boolean((this.message as any)?.forceWorkingShimmer);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const forceDone = Boolean((this.message as any)?.forceDoneHeader);
    return hasSteps || isStream || forceShimmer || forceDone;
  }

  /**
   * Maps message reasoning steps to ChatV2ReasoningStep format for child component.
   */
  public get reasoningSteps(): ChatV2ReasoningStep[] {
    return mapToV2ReasoningSteps(this.message.reasoningSteps);
  }

  /**
   * Evaluates whether the forced working shimmer is active on this message.
   */
  public get forceWorkingShimmer(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Boolean((this.message as any)?.forceWorkingShimmer);
  }

  /**
   * Evaluates whether the forced done header is active on this message.
   */
  public get forceDoneHeader(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Boolean((this.message as any)?.forceDoneHeader);
  }

  /**
   * Resolves visible actions associated with this message.
   */
  public get visibleActions(): AiAction[] {
    if (this.message.isUser) {
      return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (
      this.message.actions || (this.message as any)?.suggestedActions || []
    );
  }

  /**
   * Whether this message contains any visible actions to render.
   */
  public get hasActions(): boolean {
    return this.visibleActions.length > 0;
  }

  /**
   * Whether the message's actions are decided together (mobile parity): with more than one
   * action, each card keeps only its on/off switch and ONE bar runs or rejects the whole batch,
   * so the agent receives a single result for all of them instead of one turn per card.
   */
  public get isBatchMode(): boolean {
    return this.visibleActions.length > 1;
  }

  /** Whether the batch bar is shown: latest message, batch mode, not yet submitted, at least one action still open. */
  public get showBatchBar(): boolean {
    return (
      this.isLatest &&
      this.isBatchMode &&
      !this.batchSubmitted() &&
      this.visibleActions.some((action) => !this.isActionCompleted(action))
    );
  }

  /** Number of open actions the user kept switched on (what "Run N actions" will run). */
  public get batchRunCount(): number {
    return countRunnableActions(this.visibleActions, (index) => this.isActionActive(index), (action) => this.isActionCompleted(action));
  }

  /** Label of the batch run button, e.g. "Run 3 actions". */
  public get batchRunLabel(): string {
    return formatBatchRunLabel(this.batchRunCount, CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts);
  }

  /**
   * Computed: Formatted tooltip listing all active actions to be executed.
   */
  public readonly executeAllTooltip = computed(() => {
    const active = this.visibleActions.filter(
      (action, index) =>
        this.isActionActive(index) && !this.isActionCompleted(action),
    );
    return buildExecuteAllTooltip(active);
  });

  /**
   * Label for the Execute All button (e.g. "Execute All (3)" or "Execute Action").
   */
  public get executeAllLabel(): string {
    return formatExecuteAllLabel(
      this.batchRunCount,
      CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts,
    );
  }

  /**
   * Runs every open action of the message at once: switched-on actions run, switched-off server
   * actions are reported as declined, switched-off endpoint actions are skipped. Endpoint and
   * server actions go to the executor in ONE call (one result message for the agent); workflow
   * runs are confirmed like a single card's confirmation.
   */
  public runAllActions(): void {
    this.submitBatch(true);
  }

  /**
   * Executes all active (switched-on) actions in the batch.
   * Alias for runAllActions() for backwards compatibility and parity with ChatMessageBubbleComponent.
   */
  public executeAllActiveActions(): void {
    this.runAllActions();
  }

  /** Declines every open action of the message at once (one decision for the agent). */
  public rejectAllActions(): void {
    this.inactiveActionIndices.set(new Set(this.visibleActions.map((_action, index) => index)));
    this.submitBatch(false);
  }

  /**
   * Discards all active actions in the batch.
   * Alias for rejectAllActions() for backwards compatibility and parity with ChatMessageBubbleComponent.
   */
  public discardAllActiveActions(): void {
    this.rejectAllActions();
  }

  /**
   * Sends the batch decision.
   *
   * @param run True to run the switched-on actions, false to decline everything.
   */
  private submitBatch(run: boolean): void {
    if (!this.showBatchBar) {
      return;
    }
    const decided = toBatchDecisions(this.visibleActions, (index) => run && this.isActionActive(index), (action) => this.isActionCompleted(action));
    const executorActions = decided
      .filter(
        ({ action }) =>
          action.type === 'endpoint' ||
          action.payload?.http != null ||
          isBaseServerAction(action),
      )
      .map(({ action, isActive }) => ({ ...action, isActive }));
    if (executorActions.length > 0) {
      this.store.dispatch(
        AiChatActions.executeEndpointActions({
          actions: executorActions,
          messageId: this.message.id,
          originatingRequestMessageId: this.message.originatingRequestMessageId,
        })
      );
    }
    decided
      .filter(({ action, isActive }) => isActive && isWorkflowRun(action))
      .forEach(({ action }) => this.confirmWorkflowRun(action));
    decided.forEach(({ action, index, isActive }) => this.onExecute.emit({ action, confirmed: isActive, index, options: undefined }));
    this.batchSubmitted.set(true);
    this.cdr.markForCheck();
  }

  /**
   * Confirms one workflow-run action with the hidden confirmation message (same as a card's Confirm).
   *
   * @param action The workflow-run action.
   */
  private confirmWorkflowRun(action: AiAction): void {
    this.store.dispatch(
      AiChatActions.setActionExecutionState({
        messageId: this.message.id,
        actionDescription: action.description,
        executionState: {
          status: 'success',
          response: { confirmed: true },
        },
      }),
    );

    this.store.dispatch(
      AiChatActions.sendMessage({
        text: action.description || CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts.workflowExecutionConfirmed,
        messageType: 'action-response',
        isConfirmation: true,
        isShownInChat: false,
        isHidden: true,
        data: {
          actionResult: [
            {
              status: 'success',
              confirmed: true,
              action: action.description,
              response: { confirmed: true },
            },
          ],
          originatedActionMessageId: this.message.id,
          actionIndex: 0,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any)
    );
  }

  /**
   * Whether this message has interactive form/option elements to render.
   */
  public get hasInteractions(): boolean {
    return Boolean(this.message?.interactionConfig);
  }

  /**
   * Whether this message has any visible attachments.
   */
  public get hasAttachments(): boolean {
    return this.visibleAttachments.length > 0;
  }

  /**
   * Resolves visible file attachments (filtering out voice recording audio).
   */
  public get visibleAttachments(): FileAttachment[] {
    return filterVisibleAttachments(
      this.message?.attachments,
      this.message?.isVoiceMessage,
      this.message?.voiceFileId,
    );
  }

  /**
   * Whether this message has tool calls to render.
   */
  public get hasToolCalls(): boolean {
    return (
      !this.message.isUser &&
      Boolean(this.message.toolCalls && this.message.toolCalls.length > 0)
    );
  }

  /**
   * Whether this message has async progress tracking.
   */
  public get hasAsyncProgress(): boolean {
    return !this.message.isUser && Boolean(this.message.asyncProgress);
  }

  /**
   * Formatted timestamp string for display in the footer toolbar.
   */
  public get formattedTime(): string {
    return formatBubbleTimestamp(this.message?.timestamp);
  }

  /**
   * Helper function binding for workflow task visual themes.
   */
  public readonly getTaskTheme = getWorkflowTaskTheme;

  /**
   * Helper function binding for endpoint action detection.
   */
  public readonly hasEndpointAction = hasEndpointAction;

  /**
   * Track whether a workflow-step card's body is collapsed.
   */
  public readonly isWorkflowStepCollapsed = signal<boolean>(false);

  /**
   * Toggles the collapse state of a workflow-step progress card.
   */
  public toggleWorkflowStepCollapse(): void {
    this.isWorkflowStepCollapsed.update((v) => !v);
  }

  /**
   * Reactive loading status for project models when displaying entity data.
   */
  public readonly isGridLoading = computed(() => {
    const entityName = this.message?.entityData?.entityName;
    if (!entityName) return false;
    return this.store.selectSignal(selectLoadingModels)()[entityName] || false;
  });

  /**
   * Computes the ListGrid configuration dynamically based on the project model definition.
   */
  public readonly gridConfig = computed(() => {
    const entityData = this.message?.entityData;
    if (!entityData) return null;

    const entityName = entityData.entityName;
    const model = this.store.selectSignal(selectProjectModels)()[entityName];
    const loading = this.store.selectSignal(selectLoadingModels)()[entityName];

    if (model) {
      const configObj = extractListGridConfig(model);
      if (configObj) return configObj;
    }

    if (!loading && Array.isArray(entityData.data)) {
      return generateDefaultListGridConfig(entityData.data);
    }
    return null;
  });

  /**
   * Computes whether the current workflow plan has been confirmed/executed in this thread.
   */
  public readonly isWorkflowConfirmed = computed(() => {
    return this.checkWorkflowConfirmation(
      this.message,
      this.store.selectSignal(selectMessages)(),
    );
  });

  /**
   * Scans subsequent messages in thread to check if user confirmed the plan.
   */
  private checkWorkflowConfirmation(
    currentMsg: AIChatMessage | null,
    allMessages: AIChatMessage[],
  ): boolean {
    if (!currentMsg || !currentMsg.workflowPlan) return false;

    const currentIndex = allMessages.findIndex((m) => m.id === currentMsg.id);
    if (currentIndex === -1) return false;

    for (let i = currentIndex + 1; i < allMessages.length; i++) {
      const nextMsg = allMessages[i];
      if (
        nextMsg.type === 'action-response' &&
        (nextMsg as any).isConfirmation
      ) {
        return true;
      }
    }
    return false;
  }

  /** Local animation completion flag to avoid mutating frozen NgRx store objects directly */
  private _localHasBeenAnimated = false;

  /**
   * Whether the message has already completed its entrance animation.
   */
  public get isMessageAnimated(): boolean {
    return Boolean(
      this._localHasBeenAnimated || this.message?.hasBeenAnimated,
    );
  }

  /**
   * Safely marks the message as having completed its animation without throwing on frozen NgRx store objects.
   */
  private markMessageAnimated(): void {
    this._localHasBeenAnimated = true;
    if (this.message && Object.isExtensible(this.message)) {
      try {
        this.message.hasBeenAnimated = true;
      } catch {
        // Object is frozen by NgRx store immutability check; _localHasBeenAnimated tracks it safely.
      }
    }
  }

  /**
   * Whether attachments are cleared to be shown (respects animation pipeline for AI messages).
   */
  public get canShowAttachments(): boolean {
    if (
      this.message?.isUser ||
      this.isMessageAnimated ||
      !this.isLatest
    ) {
      return true;
    }
    return this.pipeline.showAttachments();
  }

  /**
   * Whether interactions are cleared to be shown (respects animation pipeline for AI messages).
   */
  public get canShowInteractions(): boolean {
    if (
      this.message?.isUser ||
      this.isMessageAnimated ||
      !this.isLatest
    ) {
      return true;
    }
    return this.pipeline.showInteractions();
  }

  /**
   * Whether entity data is cleared to be shown (respects animation pipeline for AI messages).
   */
  public get canShowData(): boolean {
    if (
      this.message?.isUser ||
      this.isMessageAnimated ||
      !this.isLatest
    ) {
      return true;
    }
    return this.pipeline.showData();
  }

  /**
   * Whether workflow plan cards are cleared to be shown (respects animation pipeline for AI messages).
   */
  public get canShowWorkflowPlan(): boolean {
    if (
      this.message?.isUser ||
      this.isMessageAnimated ||
      !this.isLatest
    ) {
      return true;
    }
    return this.pipeline.showWorkflowPlan();
  }

  /**
   * Whether action cards are cleared to be shown (respects animation pipeline for AI messages).
   */
  public get canShowActions(): boolean {
    if (
      this.message?.isUser ||
      this.isMessageAnimated ||
      !this.isLatest
    ) {
      return true;
    }
    return this.pipeline.showActions();
  }

  /**
   * Evaluates whether the message has any animatable content (matching Flutter _hasAnimatableContent).
   */
  public get hasAnimatableContent(): boolean {
    if ((this.message?.text || '').trim().length > 0) return true;
    if (this.hasActions) return true;
    if (this.hasInteractions) return true;
    if (this.hasAttachments) return true;
    if (this.message?.entityData) return true;
    if (this.message?.workflowPlan) return true;
    if (this.message?.jsonataTransformation) return true;
    if (this.message?.template || (this.message as any)?.displayTemplate)
      return true;
    if (this.message?.displayComponent) return true;
    return false;
  }

  /**
   * Whether the message contains an HTML/Handlebars dynamic template.
   */
  public get hasTemplate(): boolean {
    return Boolean(
      this.message?.template || (this.message as any)?.displayTemplate,
    );
  }

  /**
   * The template string to pass to the TemplateRendererComponent.
   */
  public get templateContent(): string {
    return (
      this.message?.template || (this.message as any)?.displayTemplate || ''
    );
  }

  /**
   * Evaluates whether the message should actively run its animation pipeline (matching Flutter _shouldAnimate).
   */
  public get shouldAnimate(): boolean {
    if (this.message?.isUser) {
      return false;
    }
    if (this.isMessageAnimated || (this.message as any)?.isOldMessage) {
      return false;
    }
    if (!this.isLatest) {
      return false;
    }
    if (this.isStreaming) {
      return false;
    }
    if (this.shouldAnimateReasoning) {
      return false;
    }
    return this.hasAnimatableContent;
  }

  /**
   * Evaluates whether the message is awaiting its animation turn or waiting for stream/reasoning to finish
   * (matching Flutter _isPendingAnimation).
   */
  public get isPendingAnimation(): boolean {
    if (this.message?.isUser) {
      return false;
    }
    if (this.isMessageAnimated || (this.message as any)?.isOldMessage) {
      return false;
    }
    if (!this.hasAnimatableContent) {
      return false;
    }
    // Only the active/latest message can be pending (e.g. actively streaming or playing reasoning)
    return this.isLatest && (this.isStreaming || this.shouldAnimateReasoning);
  }

  /**
   * Initializes animation pipeline and loads project model if entityData is present.
   */
  public ngOnInit(): void {
    if (this.message?.entityData?.entityName) {
      const name = this.message.entityData.entityName;
      const globalModels = this.store.selectSignal(selectProjectModelsList)();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const existsGlobally = globalModels.some(
        (m: any) =>
          (m.name && m.name.toLowerCase() === name.toLowerCase()) ||
          (m.dataTableName &&
            m.dataTableName.toLowerCase() === name.toLowerCase()),
      );
      const cachedModels = this.store.selectSignal(selectProjectModels)();
      const cachedLoadings = this.store.selectSignal(selectLoadingModels)();
      if (!existsGlobally && !cachedModels[name] && !cachedLoadings[name]) {
        this.store.dispatch(
          AiChatActions.loadProjectModel({ entityName: name }),
        );
      }
    }
    this.checkAndTriggerAnimation();
  }

  /**
   * Manages animation pipeline triggers when message or stream state changes.
   *
   * @param changes - Detected component input changes.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['message']) {
      const prevMsg = changes['message'].previousValue as AIChatMessage | undefined;
      const currMsg = changes['message'].currentValue as AIChatMessage | undefined;
      if (prevMsg?.id !== currMsg?.id) {
        this._localHasBeenAnimated = Boolean(currMsg?.hasBeenAnimated);
        this.batchSubmitted.set(false);
        this.inactiveActionIndices.set(new Set());
      }
    }
    this.checkAndTriggerAnimation();
  }

  /**
   * Cleans up local Object URL and animation timers on component destruction.
   */
  public ngOnDestroy(): void {
    this.pipeline.stop();
    const url = this.localVoiceUrl();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Whether reasoning steps should be animated with 900ms throttled playback.
   */
  public get shouldAnimateReasoning(): boolean {
    return (
      !this.message?.isUser &&
      this.isLatest &&
      !this.isMessageAnimated &&
      this.reasoningSteps.length > 0
    );
  }

  /**
   * Handles completion of reasoning playback and begins the typewriter text reveal.
   */
  public handleReasoningPlaybackComplete(): void {
    if (this.pipeline.phase() === 'idle') {
      const text = this.message?.text || '';
      this.pipeline.start(
        text,
        true,
        () => {
          this.markMessageAnimated();
          this.onAnimationComplete.emit(this.message);
          this.cdr.markForCheck();
        },
        () => {
          this.onTypingProgress.emit();
          this.cdr.markForCheck();
        },
      );
      this.cdr.markForCheck();
    }
  }

  /**
   * Checks if this message qualifies for typewriter entrance animation and starts the pipeline.
   * Directly implements Flutter message_bubble.dart animation lifecycle (_shouldAnimate vs _isPendingAnimation).
   */
  private checkAndTriggerAnimation(): void {
    if (this.message?.isUser) {
      if (this.pipeline.phase() === 'idle') {
        this.pipeline.start(this.message?.text || '', false);
      }
      return;
    }

    const text = this.message?.text || '';

    // 1. Ready to animate (matches Flutter _shouldAnimate)
    if (this.shouldAnimate) {
      if (this.pipeline.phase() === 'idle') {
        this.pipeline.start(
          text,
          true,
          () => {
            this.markMessageAnimated();
            this.onAnimationComplete.emit(this.message);
            this.cdr.markForCheck();
          },
          () => {
            this.onTypingProgress.emit();
            this.cdr.markForCheck();
          },
        );
      }
      return;
    }

    // 2. Pending animation (matches Flutter _isPendingAnimation)
    // DO NOT mark hasBeenAnimated = true! Keep pipeline ready to animate when stream/reasoning finishes.
    if (this.isPendingAnimation) {
      return;
    }

    // 3. Historical or non-animated message (matches Flutter _showCompleteMessage)
    if (this.pipeline.phase() === 'idle') {
      this.pipeline.start(text, false);
      this.markMessageAnimated();
    }
  }

  /**
   * Handles user click on the message bubble to instantly skip typewriter animation.
   */
  public handleBubbleClick(): void {
    if (this.pipeline.isTyping()) {
      this.pipeline.skipToEnd();
      this.markMessageAnimated();
      this.onAnimationComplete.emit(this.message);
      this.onTypingProgress.emit();
      this.cdr.markForCheck();
    }
  }

  /**
   * Handles action execution event emitted by a child ChatV2ActionCardComponent.
   * Dispatches endpoint actions or server actions without creating redundant visible chat messages.
   *
   * @param event - Action execution event from ChatV2ActionCard.
   * @param index - Index of the action within visibleActions.
   */
  public handleActionExecute(
    event: ChatV2ActionExecuteEvent,
    index: number,
  ): void {
    if (!this.isLatest) {
      return;
    }
    const action = event.action;
    const confirmed = event.confirmed;

    this.onExecute.emit({
      action,
      confirmed,
      index,
      options: event.options,
    });

    if (!action) {
      return;
    }

    if (confirmed) {
      // 1. Endpoint Actions & Server Actions
      if (action.type === 'endpoint' || isBaseServerAction(action)) {
        this.store.dispatch(
          AiChatActions.executeEndpointActions({
            actions: [action],
            messageId: this.message.id,
            originatingRequestMessageId:
              this.message.originatingRequestMessageId,
          }),
        );
      } else if (action.type === 'workflow' || action.isWorkflowRunAction) {
        // 2. Workflow runs: dispatch hidden message (isShownInChat: false, isHidden: true)
        // Strictly NO visible chat bubble like "User confirmed execution."
        this.store.dispatch(
          AiChatActions.setActionExecutionState({
            messageId: this.message.id,
            actionDescription: action.description,
            actionIndex: index,
            executionState: {
              status: 'success',
              response: { confirmed: true },
            },
          }),
        );

        this.store.dispatch(
          AiChatActions.sendMessage({
            text:
              action.description ||
              CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts.workflowExecutionConfirmed,
            messageType: 'action-response',
            isConfirmation: true,
            isShownInChat: false,
            isHidden: true,
            data: {
              actionResult: [
                {
                  status: 'success',
                  confirmed: true,
                  action: action.description,
                  actionIndex: index,
                  response: { confirmed: true },
                },
              ],
              originatedActionMessageId: this.message.id,
              actionIndex: index,
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any),
        );
      }
    } else {
      // Cancellation / Discard flow
      this.inactiveActionIndices.update((set) => {
        const updated = new Set(set);
        updated.add(index);
        return updated;
      });

      if (isBaseServerAction(action)) {
        const activeThreadId = this.activeThreadIdSignal();
        this.workflowExecutionService.cancelServerAction(
          action,
          this.message.id,
          activeThreadId,
          index,
          this.message.originatingRequestMessageId,
        );
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Handles action active toggle event emitted by a child ChatV2ActionCardComponent.
   * Only permits toggling while this message is the latest message and before batch submission.
   *
   * @param event - Toggle event with action and isActive boolean.
   * @param index - Index of the action within visibleActions.
   */
  public handleActionToggle(
    event: ChatV2ActionToggleEvent,
    index: number,
  ): void {
    if (!this.isLatest || this.batchSubmitted()) {
      return;
    }

    this.inactiveActionIndices.update((set) => {
      const updated = new Set(set);
      if (event.isActive) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });

    if (
      event.isActive &&
      event.action?.executionState?.response?.confirmed === false
    ) {
      this.store.dispatch(
        AiChatActions.setActionExecutionState({
          messageId: this.message.id,
          actionDescription: event.action.description,
          actionIndex: index,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          executionState: undefined as any,
        }),
      );
    }

    this.onActionToggle.emit({
      action: event.action,
      isActive: event.isActive,
      index,
    });

    this.cdr.markForCheck();
  }

  /**
   * Checks whether a specific action index is currently active (not skipped).
   *
   * @param index - Action index.
   * @returns True if active, false if toggled off.
   */
  public isActionActive(index: number): boolean {
    return !this.inactiveActionIndices().has(index);
  }

  /**
   * Determines whether an action has already reached a completed execution state.
   *
   * @param action - Action instance to inspect.
   * @returns True if status is 'success' or 'error'.
   */
  public isActionCompleted(action: AiAction): boolean {
    return (
      action.executionState?.status === 'success' ||
      action.executionState?.status === 'error'
    );
  }

  /**
   * Copies the message markdown/text content to the system clipboard.
   */
  public async copyMessageText(): Promise<void> {
    if (!this.message?.text) {
      return;
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(this.message.text);
        this.isCopied.set(true);
        setTimeout(() => {
          this.isCopied.set(false);
          this.cdr.markForCheck();
        }, CHAT_V2_MESSAGE_BUBBLE_CONFIG.clipboardTimeoutMs);
        this.cdr.markForCheck();
      }
    } catch (e) {
      console.error('Failed to copy message text:', e);
    }
  }

  /**
   * Loads the audio blob from the backend service for voice messages.
   */
  public loadAudioBlob(): void {
    if (!this.message.voiceFileId || this.isLoadingAudio()) {
      return;
    }

    this.isLoadingAudio.set(true);
    this.audioError.set(false);
    this.autoPlayAudio.set(true);

    const fetchUrl = `${environment.apiBaseUrl}/project-files/get-file-content/${this.message.voiceFileId}`;

    this.http.get(fetchUrl, { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        try {
          this.localVoiceUrl.set(createPlayableAudioUrl(blob));
          this.isLoadingAudio.set(false);
          this.cdr.markForCheck();
        } catch (e) {
          console.error('Failed to create Object URL:', e);
          this.handleAudioError();
        }
      },
      error: (err) => {
        console.error('Failed to fetch audio blob:', err);
        this.handleAudioError();
      },
    });
  }

  /**
   * Handles audio fetch failure gracefully.
   */
  private handleAudioError(): void {
    this.isLoadingAudio.set(false);
    this.audioError.set(true);
    this.notificationService.createNotification(
      'warning',
      CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts.audioUnavailableTitle,
      CHAT_V2_MESSAGE_BUBBLE_CONFIG.texts.voiceExpiredError,
    );
    this.cdr.markForCheck();
  }

  /**
   * Handles user click on a file attachment chip.
   * Opens full-screen preview if browser previewable, or triggers download.
   *
   * @param file - File attachment clicked.
   */
  public handleAttachmentClick(file: FileAttachment): void {
    if (this.isPreviewable(file)) {
      this.previewFile.set(file);
      this.isPreviewModalVisible.set(true);
    } else {
      this.downloadAttachment(file);
    }
    this.cdr.markForCheck();
  }

  /**
   * Evaluates if a file attachment can be rendered directly in the browser modal.
   *
   * @param file - File attachment to evaluate.
   * @returns True if previewable.
   */
  public isPreviewable(file: FileAttachment): boolean {
    return isBrowserPreviewable(
      file.mimeType,
      file.name,
      file.extension,
      file.url,
    );
  }

  /**
   * Triggers a forced download of the file attachment.
   *
   * @param file - File to download.
   */
  public async downloadAttachment(file: FileAttachment): Promise<void> {
    if (file.url) {
      await forceDownloadFile(file.url, file.name || 'download');
    }
  }

  /**
   * Closes the attachment preview modal.
   */
  public closeAttachmentPreview(): void {
    this.isPreviewModalVisible.set(false);
    this.previewFile.set(null);
    this.cdr.markForCheck();
  }

  /**
   * Handles interaction widget submission.
   *
   * @param selectedOptions - The options selected by the user.
   */
  public submitInteraction(selectedOptions: InteractionOption[]): void {
    if (!this.message.interactionConfig) {
      return;
    }
    this.store.dispatch(
      AiChatActions.sendInteractionResponse({
        messageId: this.message.id,
        allOptions: this.message.interactionConfig.options,
        selectedOptions,
      }),
    );
  }

  /**
   * Handles skipping an interaction widget.
   */
  public skipInteraction(): void {
    if (!this.message.interactionConfig) {
      return;
    }
    this.store.dispatch(
      AiChatActions.skipInteraction({
        messageId: this.message.id,
      }),
    );
  }

  /**
   * Helper function resolving file icon CSS class.
   *
   * @param file - File attachment.
   * @returns FontAwesome icon CSS class.
   */
  public getFileIcon(file: FileAttachment): string {
    return getFileIconClass(file.mimeType, file.name);
  }

  /**
   * Helper function formatting file sizes into human-readable strings.
   *
   * @param size - File size in bytes.
   * @returns Formatted file size string (e.g. '1.2 MB').
   */
  public formatSize(size?: number): string {
    return size !== undefined ? formatFileSize(size) : '';
  }

  /**
   * Opens an attachment URL directly in a new browser tab.
   *
   * @param file - File attachment to open.
   * @param event - Mouse event.
   */
  public openInNewTab(file: FileAttachment, event: Event): void {
    event.stopPropagation();
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }

  /**
   * Triggers forced download of file attachment from the file chip.
   *
   * @param file - File attachment.
   * @param event - Mouse event.
   */
  public async triggerDownload(
    file: FileAttachment,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    if (file.url) {
      await forceDownloadFile(file.url, file.name || 'download');
    }
  }

  /**
   * Share file to the Home dashboard as a WorkItem report.
   *
   * @param file - The file attachment to share
   * @param event - Mouse click event
   */
  public shareToHome(file: FileAttachment, event: Event): void {
    event.stopPropagation();

    if (!this.workItemService) {
      this.notificationService.createNotification(
        'error',
        'Error',
        'WorkItem service is unavailable',
      );
      return;
    }

    const projectId =
      this.store.selectSignal(selectActiveThreadId)() || 'unknown-project';
    const dedupeKey = `shared-file:${file.id}`;

    this.workItemService
      .create({
        body: {
          source: { type: 'projectFile', typeId: file.id },
          kind: 'report',
          title: `Shared File: ${file.name}`,
          bodyMarkdown: `This file was shared from the AI assistant chat.\n\n**File Name**: ${file.name}\n**Size**: ${this.formatSize(file.size)}`,
          occurredAt: new Date().toISOString(),
          dedupeKey: dedupeKey,
          needsAttention: false,
          status: 'pending',
          projectId: projectId,
        } as any,
      })
      .subscribe({
        next: () => {
          this.notificationService.createNotification(
            'success',
            'Success',
            `${file.name} has been shared to the home page.`,
          );
        },
        error: (err: any) => {
          this.notificationService.createNotification(
            'error',
            'Error',
            `Failed to share file: ${err.message || 'Unknown error'}`,
          );
        },
      });
  }

  /**
   * Dispatches active insight to the Main Canvas to view the entity records.
   */
  public onShowInCanvas(): void {
    const entityData = this.message?.entityData;
    if (!entityData) return;

    // Extract Record IDs dynamically (support both MongoDB _id and SQL id)
    const recordIds = (entityData.data || [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((r: any) => r.id || r._id)
      .filter(Boolean);

    const insightPayload = {
      id: this.message.id,
      timestamp: this.message.timestamp,
      operationType: 'READ',
      title: `${entityData.entityName}`,
      description: this.message.text,
      projectModelName: entityData.entityName,
      recordIds: recordIds,
      isSuccess: true,
      payloadData: null,
    };

    this.store.dispatch(
      ProjectDataActions.setActiveInsight({ insight: insightPayload }),
    );
  }

  /**
   * Shows the resulting entity directly in the canvas when clicking an action-response chip.
   */
  public showActionResponseInCanvas(): void {
    const actionData = this.message?.actionResponseData;
    if (!actionData) return;

    const entityName = actionData.entityName;
    const responsePayload = actionData.response;

    let recordId = null;
    if (responsePayload) {
      if (Array.isArray(responsePayload) && responsePayload.length > 0) {
        recordId = responsePayload[0].id || responsePayload[0]._id;
      } else {
        recordId = responsePayload.id || responsePayload._id;
      }
    }

    if (!entityName || !recordId) return;

    const insightPayload = {
      id: generateTempId(),
      timestamp: this.message.timestamp,
      operationType: 'READ',
      title: entityName,
      description: this.message.text,
      projectModelName: entityName,
      recordIds: [recordId],
      isSuccess: actionData.actionResponseStatus === 'success',
      payloadData: null,
    };

    this.store.dispatch(
      ProjectDataActions.setActiveInsight({ insight: insightPayload }),
    );
  }

  /**
   * Confirms and dispatches workflow plan execution.
   *
   * @param plan - The workflow plan to execute.
   */
  public executeWorkflowPlan(plan: WorkflowPlan): void {
    if (!plan) return;

    this.store.dispatch(
      AiChatActions.sendMessage({
        text: $localize`:@@aiChat.userConfirmedExecution:User confirmed execution.`,
        messageType: 'action-response',
        isConfirmation: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any),
    );

    setTimeout(() => {
      this.store.dispatch(
        AiChatActions.executeWorkflowPlan({
          plan: plan,
          messageId: this.message.id,
        }),
      );
    }, 50);
  }

  /**
   * Dispatches manual execution of JSONata transformation expressions.
   */
  public executeJsonata(): void {
    if (this.message?.jsonataTransformation) {
      this.store.dispatch(
        AiChatActions.executeJsonata({
          messageId: this.message.id,
          expression: this.message.jsonataTransformation,
        }),
      );
    }
  }

  /**
   * Dispatches request to fetch detailed workflow execution logs.
   *
   * @param correlationId - Correlation ID of the workflow execution.
   */
  public onFetchLogs(correlationId: string): void {
    if (correlationId) {
      this.store.dispatch(
        AiChatActions.fetchWorkflowLogs({
          messageId: this.message.id,
          correlationId: correlationId,
        }),
      );
    }
  }

  /**
   * Saves the AI message content and tabular data to spaces.
   */
  public saveToSpace(): void {
    if (!this.message.text) return;

    const activeThreadId = this.store.selectSignal(selectActiveThreadId)();
    const threads = this.store.selectSignal(selectThreads)();
    const activeThread = threads.find((t) => t.id === activeThreadId);
    const threadTitle = activeThread?.title || 'Unknown Thread';

    const now = new Date();
    const dateOpts: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    const timeOpts: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
    };
    const formattedDate = now.toLocaleDateString('en-US', dateOpts);
    const formattedTime = now
      .toLocaleTimeString('en-US', timeOpts)
      .toLowerCase()
      .replace(' ', '');

    let contentToSave = `${this.message.text}\n\n`;

    if (
      this.message.entityData &&
      Array.isArray(this.message.entityData.data) &&
      this.message.entityData.data.length > 0
    ) {
      const data = this.message.entityData.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const headers = Array.from(
        new Set(data.flatMap((item: any) => Object.keys(item))),
      );

      contentToSave += `**Data Results: ${this.message.entityData.entityName}**\n\n`;
      contentToSave += `| ${headers.join(' | ')} |\n`;
      contentToSave += `| ${headers.map(() => '---').join(' | ')} |\n`;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data.forEach((item: any) => {
        const row = headers.map((header) => {
          const val = item[header];
          if (val === null || val === undefined) return '';
          if (typeof val === 'object')
            return JSON.stringify(val).replace(/\|/g, '\\|');
          return String(val).replace(/\|/g, '\\|');
        });
        contentToSave += `| ${row.join(' | ')} |\n`;
      });
      contentToSave += '\n';
    }

    contentToSave += `> Saved from "${threadTitle}" on ${formattedDate} at ${formattedTime}`;

    if (this.browserStorage) {
      this.browserStorage.setItem('pendingAiSaveContent', contentToSave);
    }
    if (this.router) {
      this.router.navigate(['/spaces/save-to-page']);
    }
  }
}
